import { v4 as uuidv4 } from "uuid";
import i18n from "../../../i18n";
import Point from "./point";
import { mapApiPointToIdTypePoint } from "./base-zone";
import ZonePointType from "../../../enums/robot/rooms/zone-point-type";
import ValidationResult from "../../api/validation-result";

export interface WaypointEdge {
  id: string;
  room: string;
  u: string;
  v: string;
}

export interface WaypointNode {
  id: string;
  gateId: string;
  room: string;
  point: Point;
}

export interface WaypointSet {
  edges: WaypointEdge[];
  nodes: WaypointNode[];
}

export interface WaypointNodeApi {
  id: string;
  point: number[];
  room: string;
  gateId: string;
}

export interface WaypointEdgeApi{
  id?: string;
  room: string;
  u: string;
  v: string;
}

export const mapEdgeApiToEdge = (apiData: WaypointEdgeApi) => ({
  ...apiData,
  id: apiData.id ?? `${apiData.u}_${apiData.v}`,
})

export enum WaypointPart {
  node = "node",
  edge = "edge"
}

export const WAYPOINT_COLOR = "rgb(254,162,2)";

export const copyNode = (node: WaypointNode) => {
  return {
    ...node,
    point: { ...node.point },
  } as WaypointNode;
};

export const createEdge = (edges: WaypointEdge[], initialNode: WaypointNode, endNode: WaypointNode, roomId: string) => {
  if (
    edges.find(
      (e) => (e.u === initialNode.id && e.v === endNode.id) || (e.v === initialNode.id && e.u === endNode.id)
    ) != null
  ) {
    return null;
  }
  return { u: initialNode.id, v: endNode.id, id: uuidv4(), room: roomId } as WaypointEdge;
};

export const mapNodeApiToNode = (apiData: WaypointNodeApi) => ({
  ...apiData,
  point: mapApiPointToIdTypePoint(apiData.point, ZonePointType.WayPoint, apiData.id)!,
});
export const mapNodeToNodeApi = (node: WaypointNode) => ({
  ...node,
  point: [node.point.coordinateX, node.point.coordinateY, node.point.coordinateTheta ?? 0],
});
export const mapPartialNodeToApi = (node: Partial<WaypointNode>) => ({
  ...node,
  point:
    node.point != null ? [node.point.coordinateX, node.point.coordinateY, node.point.coordinateTheta ?? 0] : undefined,
});

export interface WaypointSetValidationResult {
  room: string,
  waypointSet: { nodes: WaypointNode[], edges: WaypointEdge[] }
  validationErrors: ValidationResult[]
};

export const isUnsavedWaypointSet = (payload: any): payload is WaypointSetValidationResult => {
  if (!payload || typeof payload !== 'object') return false;
  return (
    typeof payload.room === 'string' &&
    typeof payload.waypointSet === 'object' &&
    Array.isArray(payload.waypointSet.nodes) &&
    Array.isArray(payload.waypointSet.edges) &&
    Array.isArray(payload.validationErrors)
  );
};

export const validateWayPoints = (
  wayPointSet: { nodes: WaypointNode[]; edges: WaypointEdge[]; }
): ValidationResult => {
  if (wayPointSet.nodes.length === 0 && wayPointSet.edges.length === 0) {
    return { isValid: true, error: null };
  }
  if (wayPointSet.nodes.length === 1) {
    return { isValid: false, error: i18n.t("validation-error-waypoint-needs-multiple-points") };
  }

  if (
    wayPointSet.edges.some(
      (e) => !wayPointSet.nodes.some((n) => n.id === e.u) || !wayPointSet.nodes.some((n) => n.id === e.v)
    )
  ) {
    return { isValid: false, error: i18n.t("validation-error-waypoint-edges-nonexistent-nodes") };
  }

  let connectedNodes: string[] = [];
  if (wayPointSet.nodes.length > 0) {
    connectedNodes.push(wayPointSet.nodes[0].id);
    for (let currentNodeIndex = 0; currentNodeIndex < connectedNodes.length; currentNodeIndex++) {
      const currentNode = connectedNodes[currentNodeIndex];
      const nextNodes = wayPointSet.nodes
        .filter((n) =>
          wayPointSet.edges.some(
            (e) =>
              ((e.u === n.id && e.v === currentNode) ||
                (e.v === n.id && e.u === currentNode)) &&
              n.id !== currentNode
          )
        )
        .map((n) => n.id);

      nextNodes.forEach((n) => {
        if (!connectedNodes.includes(n)) {
          connectedNodes.push(n);
        }
      });

      if (connectedNodes.length === wayPointSet.nodes.length) {
        break;
      }
    }
  }
  if (connectedNodes.length !== wayPointSet.nodes.length) {
    return { isValid: false, error: i18n.t("validation-error-waypoint-separated-nodes") };
  }

  if (
    wayPointSet.edges.some(
      (edge) =>
        wayPointSet.edges.filter(
          (e) =>
            (edge.u === e.u && edge.v === e.v) ||
            (edge.u === e.v && edge.v === e.u)
        ).length > 1
    )
  ) {
    return { isValid: false, error: i18n.t("validation-error-waypoint-repeated-edges-between-nodes") };
  }

  return { isValid: true, error: null };
};

type ConnectionResult = {
  from: WaypointNode;
  to: WaypointNode;
  distance: number;
};

export const findNearestNodeConnection = (
  nodeA: WaypointNode,
  nodeB: WaypointNode,
  nodes: WaypointNode[],
): ConnectionResult | null => {
  const distance = (n1: WaypointNode, n2: WaypointNode): number => {
    const dx = n1.point.coordinateX - n2.point.coordinateX;
    const dy = n1.point.coordinateY - n2.point.coordinateY;
    return Math.hypot(dx, dy);
  };

  let best: ConnectionResult | null = null;

  for (const node of nodes) {
    // Exclude A and B themselves
    if (node.id === nodeA.id || node.id === nodeB.id) continue;

    const dA = distance(nodeA, node);
    const dB = distance(nodeB, node);

    // Choose the closer endpoint for this node
    const candidate = dA <= dB ? { from: nodeA, to: node, distance: dA } : { from: nodeB, to: node, distance: dB };

    // Keep the globally closest connection
    if (best === null || candidate.distance < best.distance) {
      best = candidate;
    }
  }

  return best;
};
